import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { ArrowLeft, Shield, Upload, CheckCircle, AlertCircle } from 'lucide-react';

const OnboardingVerification = ({ data, onNext, onPrev, loading }) => {
  const [verificationData, setVerificationData] = useState({
    business_license: data?.business_license || null,
    tax_document: data?.tax_document || null,
    identity_document: data?.identity_document || null,
    verification_code: data?.verification_code || '',
    phone_verified: data?.phone_verified || false,
    email_verified: data?.email_verified || false,
  });

  const [uploading, setUploading] = useState({
    business_license: false,
    tax_document: false,
    identity_document: false,
  });

  const handleFileUpload = async (documentType, file) => {
    setUploading(prev => ({ ...prev, [documentType]: true }));
    
    try {
      // Simulate file upload
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setVerificationData(prev => ({
        ...prev,
        [documentType]: {
          name: file.name,
          size: file.size,
          uploaded_at: new Date().toISOString(),
        },
      }));
    } catch (error) {
      console.log('Error uploading file:', error);
    } finally {
      setUploading(prev => ({ ...prev, [documentType]: false }));
    }
  };

  const handleVerifyPhone = async () => {
    // Simulate phone verification
    await new Promise(resolve => setTimeout(resolve, 1000));
    setVerificationData(prev => ({
      ...prev,
      phone_verified: true,
    }));
  };

  const handleVerifyEmail = async () => {
    // Simulate email verification
    await new Promise(resolve => setTimeout(resolve, 1000));
    setVerificationData(prev => ({
      ...prev,
      email_verified: true,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onNext(verificationData);
  };

  const DocumentUpload = ({ type, label, required = false }) => (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium text-gray-700">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        {verificationData[type] && (
          <CheckCircle className="w-5 h-5 text-green-500" />
        )}
      </div>
      
      {verificationData[type] ? (
        <div className="text-sm text-gray-600">
          <p className="font-medium">{verificationData[type].name}</p>
          <p className="text-xs text-gray-500">
            Uploaded {new Date(verificationData[type].uploaded_at).toLocaleString()}
          </p>
        </div>
      ) : (
        <div className="text-center py-4">
          <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                handleFileUpload(type, file);
              }
            }}
            className="hidden"
            id={`${type}-upload`}
          />
          <label
            htmlFor={`${type}-upload`}
            className="cursor-pointer text-blue-600 hover:text-blue-500 text-sm"
          >
            {uploading[type] ? 'Uploading...' : 'Click to upload'}
          </label>
          <p className="text-xs text-gray-500 mt-1">
            PDF, JPG, PNG up to 10MB
          </p>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Shield className="w-8 h-8 text-blue-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Account Verification</h2>
        <p className="text-gray-600">
          Help us verify your account by providing the required documents and verification
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Document Upload Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Required Documents</h3>
          
          <DocumentUpload
            type="business_license"
            label="Business License"
            required
          />
          
          <DocumentUpload
            type="tax_document"
            label="Tax Registration Document"
            required
          />
          
          <DocumentUpload
            type="identity_document"
            label="Identity Document"
            required
          />
        </div>

        {/* Verification Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Contact Verification</h3>
          
          {/* Phone Verification */}
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center">
              <div className="mr-3">
                {verificationData.phone_verified ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-yellow-500" />
                )}
              </div>
              <div>
                <p className="font-medium text-gray-900">Phone Number</p>
                <p className="text-sm text-gray-600">Verify your phone number</p>
              </div>
            </div>
            {!verificationData.phone_verified && (
              <Button
                type="button"
                variant="outline"
                onClick={handleVerifyPhone}
                size="sm"
              >
                Verify
              </Button>
            )}
          </div>

          {/* Email Verification */}
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center">
              <div className="mr-3">
                {verificationData.email_verified ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-yellow-500" />
                )}
              </div>
              <div>
                <p className="font-medium text-gray-900">Email Address</p>
                <p className="text-sm text-gray-600">Verify your email address</p>
              </div>
            </div>
            {!verificationData.email_verified && (
              <Button
                type="button"
                variant="outline"
                onClick={handleVerifyEmail}
                size="sm"
              >
                Verify
              </Button>
            )}
          </div>
        </div>

        {/* Verification Code */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Verification Code (if provided)
          </label>
          <Input
            type="text"
            value={verificationData.verification_code}
            onChange={(e) => setVerificationData(prev => ({
              ...prev,
              verification_code: e.target.value
            }))}
            placeholder="Enter verification code"
          />
          <p className="text-sm text-gray-500 mt-1">
            Enter the verification code if you received one from our team
          </p>
        </div>

        {/* Terms and Conditions */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h4 className="font-medium text-gray-900 mb-2">Important Notes</h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• All documents will be reviewed within 24-48 hours</li>
            <li>• You'll receive an email notification once verification is complete</li>
            <li>• Documents are securely stored and encrypted</li>
            <li>• You can continue using basic features while verification is pending</li>
          </ul>
        </div>

        {/* Navigation */}
        <div className="flex justify-between pt-6">
          <Button
            type="button"
            variant="outline"
            onClick={onPrev}
            disabled={loading}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <Button
            type="submit"
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Complete Verification'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default OnboardingVerification;